// MOBILE-FRIENDLY: MedicalProfessionalDashboard.js with proper completed orders handling

import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Chip,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CompleteIcon,
  Person as PersonIcon,
  LocalHospital as ExamIcon,
  Route as RouteIcon,
  ExpandMore as ExpandMoreIcon,
  Reorder as SequenceIcon,
  Notes as NotesIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as CompletedIcon,
  Schedule as ActiveIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { orderService } from "../../services/orderService";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";
import { useAuth } from "../../contexts/AuthContext";

const MedicalProfessionalDashboard = () => {
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [statusFilter, setStatusFilter] = useState("assigned");
  const [completeDialog, setCompleteDialog] = useState({
    open: false,
    order: null,
  });
  const [completing, setCompleting] = useState(false);
  const [viewMode, setViewMode] = useState("routes");

  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (user) {
      fetchRouteOrders();
    }
  }, [selectedDate, statusFilter, user]);

  const fetchRouteOrders = async () => {
    if (!user) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {

      const response = await medicalProfessionalsService.getMyRouteOrders(
        statusFilter
      );

      const safeResponse = {
        routes: Array.isArray(response?.routes) ? response.routes : [],
        unassigned_orders: Array.isArray(response?.unassigned_orders)
          ? response.unassigned_orders
          : [],
        total_orders: response?.total_orders || 0,
        total_routes: response?.total_routes || 0,
        status_filter: response?.status_filter || statusFilter,
        medical_professional: response?.medical_professional || {},
      };

      // Apply date filtering
      if (selectedDate && selectedDate !== "all") {

        const normalizeDate = (dateString) => {
          if (!dateString) return null;
          const date = new Date(dateString + "T00:00:00");
          return date.toISOString().split("T")[0];
        };

        const targetDate = normalizeDate(selectedDate);

        // Filter routes
        safeResponse.routes = safeResponse.routes
          .map((route) => ({
            ...route,
            orders: route.orders.filter(
              (order) => normalizeDate(order.scheduled_date) === targetDate
            ),
          }))
          .filter((route) => route.orders.length > 0);

        // Filter unassigned orders
        safeResponse.unassigned_orders = safeResponse.unassigned_orders.filter(
          (order) => normalizeDate(order.scheduled_date) === targetDate
        );
      }

      setRouteData(safeResponse);
    } catch (err) {
      console.error("Error fetching route orders:", err);

      if (err.message.includes("permission")) {
        setError(
          "You don't have permission to view route orders. Please contact your administrator."
        );
      } else if (err.message.includes("not found")) {
        setError(
          "Your medical professional profile was not found. Please contact support."
        );
      } else if (err.message.includes("Authentication")) {
        setError("Please log in again to access your orders.");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else if (err.message.includes("Network")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Unable to load your orders at this time. Please try again.");
      }

      setRouteData({
        routes: [],
        unassigned_orders: [],
        total_orders: 0,
        total_routes: 0,
        status_filter: statusFilter,
        medical_professional: {},
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchRouteOrders();
  };

  // FIXED: Get all orders properly - for completed orders, don't separate unassigned
  const getAllOrders = () => {
    if (!routeData) return [];
    const routeOrders =
      routeData.routes?.flatMap((route) => route.orders) || [];

    // FIXED: For completed orders, include unassigned in the main list
    if (statusFilter === "completed") {
      const unassignedOrders = routeData.unassigned_orders || [];
      return [...routeOrders, ...unassignedOrders];
    } else {
      // For active orders, keep unassigned separate
      return routeOrders;
    }
  };

  // FIXED: Get only unassigned orders for active status
  const getUnassignedOrders = () => {
    if (statusFilter === "completed") {
      return []; // No unassigned section for completed orders
    }
    return routeData?.unassigned_orders || [];
  };

  const handleCompleteClick = (order) => {
    setCompleteDialog({ open: true, order });
  };

  const handleCompleteConfirm = async () => {
    if (!completeDialog.order) return;

    setCompleting(true);
    try {
      await orderService.updateOrderStatus(completeDialog.order.id, {
        status: "completed",
        notes: "Order completed by medical professional",
      });

      setSuccess("Order completed successfully!");
      setCompleteDialog({ open: false, order: null });
      await fetchRouteOrders();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error completing order:", err);
      setError(err.message || "Failed to complete order");
    } finally {
      setCompleting(false);
    }
  };

  const handleCompleteCancel = () => {
    setCompleteDialog({ open: false, order: null });
  };

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(newDate)) {
      setSelectedDate(newDate);
    } else {
      setError("Invalid date format selected");
    }
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleViewDetails = (orderId) => {
    navigate(`/medical-professional/orders/${orderId}`);
  };

  const openGoogleMaps = (address) => {
    if (address) {
      const mapsUrl = `https://www.google.com/maps/dir/Current+Location/${encodeURIComponent(
        address
      )}`;
      window.open(mapsUrl, "_blank");
    }
  };

  const makePhoneCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const getExamCount = (order) => {
    return order.exam_count || 0;
  };

  const getDateOptions = () => {
    const options = [];
    const today = new Date();

    const baseDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      const label =
        i === 0
          ? "Today"
          : i === 1
          ? "Tomorrow"
          : date.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            });

      options.push({ value: dateStr, label });
    }

    return options;
  };

  const getStatusDisplayInfo = (status) => {
    switch (status) {
      case "completed":
        return {
          label: "Completed",
          color: "success",
          icon: <CompletedIcon />,
        };
      case "assigned":
        return {
          label: "Active",
          color: "primary",
          icon: <ActiveIcon />,
        };
      default:
        return {
          label: "Active",
          color: "primary",
          icon: <ActiveIcon />,
        };
    }
  };

  // MOBILE-OPTIMIZED: Render order card with responsive design
  const renderOrderCard = (order, routeInfo = null) => {
    const examCount = getExamCount(order);
    const isCompleted = order.status === "completed";
    const statusInfo = getStatusDisplayInfo(order.status);

    return (
      <Paper
        key={order.id}
        elevation={isSmallMobile ? 1 : 2}
        sx={{
          mb: isSmallMobile ? 1.5 : 2,
          borderRadius: isSmallMobile ? 1.5 : 2,
          overflow: "hidden",
          backgroundColor: isCompleted ? "#f8f9fa" : "#fff",
          opacity: isCompleted ? 0.85 : 1,
          border: isCompleted ? "1px solid #e8f5e9" : "none",
        }}
      >
        {/* MOBILE-OPTIMIZED Header */}
        <Box
          sx={{
            p: isSmallMobile ? 1.5 : 2,
            pb: isSmallMobile ? 1 : 1,
            backgroundColor: isCompleted
              ? "#e8f5e9"
              : routeInfo
              ? "#e3f2fd"
              : "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {/* MOBILE: Stack order number and chips vertically on small screens */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isSmallMobile ? "flex-start" : "center",
              gap: isSmallMobile ? 1 : 0,
              mb: 1,
            }}
          >
            <Typography
              variant={isSmallMobile ? "body1" : "subtitle1"}
              fontWeight="bold"
              color={isCompleted ? "text.secondary" : "primary"}
            >
              {order.order_number}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Chip
                icon={statusInfo.icon}
                label={statusInfo.label}
                color={statusInfo.color}
                size="small"
                sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
              />

              {routeInfo && (
                <Chip
                  icon={<SequenceIcon />}
                  label={`#${order.route_sequence || "?"}`}
                  color="info"
                  size="small"
                  sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
                />
              )}
            </Box>
          </Box>

          {/* Route name - mobile optimized */}
          {routeInfo && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <RouteIcon sx={{ fontSize: 14, mr: 0.5, color: "#666" }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
              >
                {routeInfo.route_name}
              </Typography>
            </Box>
          )}

          {/* MOBILE: Completion date - smaller on mobile */}
          {isCompleted && order.completed_date && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <CompletedIcon sx={{ fontSize: 14, mr: 0.5, color: "#4caf50" }} />
              <Typography
                variant="caption"
                color="success.main"
                sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
              >
                Completed:{" "}
                {new Date(order.completed_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* MOBILE-OPTIMIZED Content */}
        <Box sx={{ p: isSmallMobile ? 1.5 : 2, pt: isSmallMobile ? 1 : 1.5 }}>
          {/* Patient Name - mobile optimized */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: isSmallMobile ? 1 : 1.5,
            }}
          >
            <PersonIcon sx={{ fontSize: 16, mr: 1, color: "#666" }} />
            <Typography
              variant={isSmallMobile ? "body2" : "body1"}
              fontWeight="600"
              color={isCompleted ? "text.secondary" : "text.primary"}
            >
              {order.patient_name}
            </Typography>
          </Box>

          {/* MOBILE: Address - clickable with better touch targets */}
          {/* MOBILE: Address - clickable with better touch targets */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              mb: isSmallMobile ? 1 : 1.5,
              cursor: "pointer",
              p: isSmallMobile ? 0.75 : 1,
              mx: isSmallMobile ? -0.75 : -1,
              borderRadius: 1,
              minHeight: isSmallMobile ? 40 : "auto", // Better touch target
              "&:hover": {
                backgroundColor: isCompleted ? "#f0f0f0" : "#f5f5f5",
              },
              "&:active": {
                backgroundColor: isCompleted ? "#e8e8e8" : "#eeeeee",
              },
            }}
            onClick={() => openGoogleMaps(order.patient_address)}
          >
            <LocationIcon
              sx={{ fontSize: 16, mr: 1, color: "#666", mt: 0.2 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: isSmallMobile ? "0.8rem" : "0.875rem",
                  lineHeight: isSmallMobile ? 1.3 : 1.43,
                }}
              >
                {order.patient_address}
              </Typography>
              {order.address_notes && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#ff9800",
                    fontWeight: 600,
                    fontSize: isSmallMobile ? "0.7rem" : "0.75rem",
                    display: "block",
                    mt: 0.5,
                    backgroundColor: "#fff3e0",
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.5,
                    display: "inline-block",
                  }}
                >
                  📍 {order.address_notes}
                </Typography>
              )}
            </Box>
          </Box>

          {/* MOBILE: Phone - better touch targets */}
          {order.patient_phone && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: isSmallMobile ? 1 : 1.5,
                cursor: "pointer",
                p: isSmallMobile ? 0.75 : 1,
                mx: isSmallMobile ? -0.75 : -1,
                borderRadius: 1,
                minHeight: isSmallMobile ? 40 : "auto", // Better touch target
                "&:hover": {
                  backgroundColor: isCompleted ? "#f0f0f0" : "#f5f5f5",
                },
                "&:active": {
                  backgroundColor: isCompleted ? "#e8e8e8" : "#eeeeee",
                },
              }}
              onClick={() => makePhoneCall(order.patient_phone)}
            >
              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: "#666" }} />
              <Typography
                variant="body2"
                fontWeight="500"
                color={isCompleted ? "text.secondary" : "text.primary"}
                sx={{ fontSize: isSmallMobile ? "0.8rem" : "0.875rem" }}
              >
                {order.patient_phone}
              </Typography>
            </Box>
          )}

          {/* Exam Count - mobile optimized */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: isSmallMobile ? 1 : 1.5,
            }}
          >
            <ExamIcon sx={{ fontSize: 16, mr: 1, color: "#666" }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: isSmallMobile ? "0.8rem" : "0.875rem" }}
            >
              <strong>{examCount}</strong> exam{examCount !== 1 ? "s" : ""}{" "}
              required
            </Typography>
          </Box>

          {/* Notes - mobile optimized */}
          {order.special_notes && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: isSmallMobile ? 1 : 1.5,
              }}
            >
              <NotesIcon sx={{ fontSize: 16, mr: 1, color: "#666", mt: 0.2 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  flex: 1,
                  fontSize: isSmallMobile ? "0.8rem" : "0.875rem",
                  lineHeight: isSmallMobile ? 1.3 : 1.43,
                }}
              >
                {order.special_notes}
              </Typography>
            </Box>
          )}

          {/* Service Location */}
          <Box sx={{ mb: isSmallMobile ? 1.5 : 2 }}>
            <Chip
              label={order.place_of_service || "Home"}
              color="secondary"
              size="small"
              sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
            />
          </Box>

          {/* MOBILE: Action Buttons - stack on small screens */}
          <Stack
            direction={isSmallMobile ? "column" : "row"}
            spacing={isSmallMobile ? 1 : 1}
            sx={{ mt: isSmallMobile ? 1.5 : 2 }}
          >
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => handleViewDetails(order.id)}
              size={isSmallMobile ? "medium" : "small"}
              sx={{
                flex: isSmallMobile ? "none" : 1,
                borderRadius: 2,
                minHeight: isSmallMobile ? 44 : "auto", // Better touch target
              }}
            >
              View Details
            </Button>

            {!isCompleted && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CompleteIcon />}
                onClick={() => handleCompleteClick(order)}
                size={isSmallMobile ? "medium" : "small"}
                sx={{
                  borderRadius: 2,
                  minHeight: isSmallMobile ? 44 : "auto", // Better touch target
                }}
              >
                Complete
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>
    );
  };

  const allOrders = getAllOrders();
  const unassignedOrders = getUnassignedOrders();

  return (
    <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh" }}>
      {/* MOBILE-OPTIMIZED Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          p: isSmallMobile ? 1.5 : 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: isSmallMobile ? 1.5 : 2,
          }}
        >
          <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight="bold">
            My Orders
          </Typography>

          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              color: "primary.main",
              minWidth: isSmallMobile ? 40 : 48,
              minHeight: isSmallMobile ? 40 : 48,
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* MOBILE: Stack controls vertically on small screens */}
        <Stack
          direction={isSmallMobile ? "column" : "row"}
          spacing={isSmallMobile ? 1 : 1}
          sx={{ mb: 1 }}
        >
          <FormControl
            size="small"
            sx={{ flex: 1, minWidth: isSmallMobile ? "100%" : 180 }}
          >
            <InputLabel>Date</InputLabel>
            <Select
              value={selectedDate}
              label="Date"
              onChange={handleDateChange}
              disabled={loading}
            >
              {getDateOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{ flex: 1, minWidth: isSmallMobile ? "100%" : 120 }}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleStatusChange}
              disabled={loading}
            >
              <MenuItem value="assigned">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ActiveIcon fontSize="small" />
                  Active
                </Box>
              </MenuItem>
              <MenuItem value="completed">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CompletedIcon fontSize="small" />
                  Completed
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{ flex: 1, minWidth: isSmallMobile ? "100%" : 120 }}
          >
            <InputLabel>View</InputLabel>
            <Select
              value={viewMode}
              label="View"
              onChange={(e) => setViewMode(e.target.value)}
              disabled={loading}
            >
              <MenuItem value="routes">Routes</MenuItem>
              <MenuItem value="flat">All</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* MOBILE: Stack chips and wrap on small screens */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Chip
            label={`${allOrders.length} orders total`}
            color="primary"
            size="small"
            sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
          />
          {statusFilter === "completed" && (
            <Chip
              icon={<CompletedIcon />}
              label="Showing completed orders"
              color="success"
              variant="outlined"
              size="small"
              sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
            />
          )}
          {statusFilter === "assigned" && (
            <Chip
              icon={<ActiveIcon />}
              label="Showing active orders"
              color="info"
              variant="outlined"
              size="small"
              sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.75rem" }}
            />
          )}
        </Box>
      </Box>

      {/* Messages */}
      {error && (
        <Alert
          severity="error"
          sx={{ m: isSmallMobile ? 1.5 : 2, mb: 0 }}
          onClose={() => setError("")}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ m: isSmallMobile ? 1.5 : 2, mb: 0 }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {loading && <LinearProgress />}

      {/* MOBILE-OPTIMIZED Content */}
      <Box sx={{ p: isSmallMobile ? 1.5 : 2 }}>
        {!loading && (
          <>
            {allOrders.length === 0 ? (
              <Paper
                elevation={1}
                sx={{
                  p: isSmallMobile ? 3 : 4,
                  textAlign: "center",
                  borderRadius: 2,
                }}
              >
                {statusFilter === "completed" ? (
                  <CompletedIcon
                    sx={{
                      fontSize: isSmallMobile ? 40 : 48,
                      color: "text.disabled",
                      mb: 2,
                    }}
                  />
                ) : (
                  <AssignmentIcon
                    sx={{
                      fontSize: isSmallMobile ? 40 : 48,
                      color: "text.disabled",
                      mb: 2,
                    }}
                  />
                )}
                <Typography
                  variant={isSmallMobile ? "body1" : "h6"}
                  color="text.secondary"
                  gutterBottom
                >
                  No {statusFilter === "completed" ? "completed" : "active"}{" "}
                  orders for{" "}
                  {selectedDate === new Date().toISOString().split("T")[0]
                    ? "today"
                    : selectedDate}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {statusFilter === "completed"
                    ? "Complete some orders to see them here."
                    : "Check back later or select a different date."}
                </Typography>
              </Paper>
            ) : viewMode === "routes" ? (
              <Box>
                {/* Routes */}
                {routeData?.routes?.map((route) => (
                  <Accordion
                    key={route.route_id}
                    defaultExpanded
                    sx={{ mb: isSmallMobile ? 1.5 : 2, borderRadius: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <RouteIcon
                          color="primary"
                          sx={{ fontSize: isSmallMobile ? 18 : 20 }}
                        />
                        <Typography
                          variant={isSmallMobile ? "body1" : "subtitle1"}
                          fontWeight="bold"
                        >
                          {route.route_name}
                        </Typography>
                        <Chip
                          label={`${route.orders.length}`}
                          color="primary"
                          size="small"
                          sx={{
                            fontSize: isSmallMobile ? "0.7rem" : "0.75rem",
                          }}
                        />
                        {statusFilter === "completed" && (
                          <Chip
                            icon={<CompletedIcon />}
                            label="Completed"
                            color="success"
                            size="small"
                            sx={{
                              fontSize: isSmallMobile ? "0.7rem" : "0.75rem",
                            }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {route.orders.map((order) =>
                        renderOrderCard(order, route)
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}

                {/* FIXED: Only show Unassigned for active orders */}
                {unassignedOrders.length > 0 && statusFilter === "assigned" && (
                  <Accordion sx={{ borderRadius: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <AssignmentIcon
                          color="warning"
                          sx={{ fontSize: isSmallMobile ? 18 : 20 }}
                        />
                        <Typography
                          variant={isSmallMobile ? "body1" : "subtitle1"}
                          fontWeight="bold"
                        >
                          Unassigned
                        </Typography>
                        <Chip
                          label={`${unassignedOrders.length}`}
                          color="warning"
                          size="small"
                          sx={{
                            fontSize: isSmallMobile ? "0.7rem" : "0.75rem",
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {unassignedOrders.map((order) => renderOrderCard(order))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* FIXED: For completed orders, show any unassigned orders mixed in with routes */}
                {statusFilter === "completed" &&
                  routeData?.unassigned_orders?.length > 0 && (
                    <>
                      {routeData.unassigned_orders.map((order) =>
                        renderOrderCard(order)
                      )}
                    </>
                  )}
              </Box>
            ) : (
              // Flat view - all orders as individual cards
              <Box>{allOrders.map((order) => renderOrderCard(order))}</Box>
            )}
          </>
        )}
      </Box>

      {/* MOBILE-OPTIMIZED Complete Order Dialog */}
      <Dialog
        open={completeDialog.open}
        onClose={handleCompleteCancel}
        maxWidth="sm"
        fullWidth
        sx={{ p: isSmallMobile ? 1 : 2 }}
        PaperProps={{
          sx: {
            m: isSmallMobile ? 1 : 2,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: isSmallMobile ? 1 : 2 }}>
          <Typography variant={isSmallMobile ? "h6" : "h5"}>
            Complete Order
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: isSmallMobile ? 2 : 3 }}>
          <Typography gutterBottom variant={isSmallMobile ? "body2" : "body1"}>
            Are you sure you want to mark this order as completed?
          </Typography>
          {completeDialog.order && (
            <Box
              mt={2}
              p={isSmallMobile ? 1.5 : 2}
              bgcolor="grey.100"
              borderRadius={1}
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Order:</strong> {completeDialog.order.order_number}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Patient:</strong> {completeDialog.order.patient_name}
              </Typography>
              {completeDialog.order.route_name && (
                <Typography variant="body2">
                  <strong>Route:</strong> {completeDialog.order.route_name}
                  {completeDialog.order.route_sequence &&
                    ` (Sequence #${completeDialog.order.route_sequence})`}
                </Typography>
              )}
            </Box>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 2,
              fontSize: isSmallMobile ? "0.8rem" : "0.875rem",
            }}
          >
            This action cannot be undone. The order will be marked as completed
            and moved to your completed orders list.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ p: isSmallMobile ? 2 : 3, pt: isSmallMobile ? 1 : 2 }}
        >
          <Button
            onClick={handleCompleteCancel}
            disabled={completing}
            size={isSmallMobile ? "medium" : "large"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCompleteConfirm}
            variant="contained"
            color="success"
            disabled={completing}
            size={isSmallMobile ? "medium" : "large"}
            sx={{ minWidth: isSmallMobile ? 100 : 120 }}
          >
            {completing ? "Completing..." : "Complete Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalProfessionalDashboard;
